import {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
  FocusEvent,
  KeyboardEvent,
} from 'react';
import { MegaNavMenu } from './mega-nav.constants';

function isSublink(id: string) {
  return id && id.includes('-');
}

function getActiveElement() {
  return document.activeElement ? document.activeElement.id : '';
}

function isSearchField(id: string) {
  return id === 'search';
}

export const useMegaNav = (headerNavRef: RefObject<HTMLElement>) => {
  const menuRef = useRef<MegaNavMenu>([]);

  const [primary, setPrimary] = useState<string>('');
  const [activeId, setActiveId] = useState<string>('');

  const getPrimaryMenu = useCallback(() => {
    return menuRef.current?.find(p => p.element.id === primary);
  }, [primary]);

  const nextPrimaryItem = useCallback(() => {
    const menu = getPrimaryMenu();
    if (menu && menu.position + 1 < menuRef.current.length) {
      setPrimary(menuRef.current[menu.position + 1].element.id);
      setActiveId(
        activeId ? menuRef.current[menu.position + 1].element.id : ''
      );
      menuRef.current[menu.position + 1].element.focus();
    }
  }, [activeId, getPrimaryMenu]);

  const previousPrimaryItem = useCallback(() => {
    const menu = getPrimaryMenu();
    if (menu && menu.position - 1 >= 0) {
      setPrimary(menuRef.current[menu.position - 1].element.id);
      setActiveId(
        activeId ? menuRef.current[menu.position - 1].element.id : ''
      );
      menuRef.current[menu.position - 1].element.focus();
    }
  }, [activeId, getPrimaryMenu]);

  const handleOnClick = useCallback(
    (id: string = '') => {
      setPrimary(id);
      setActiveId(id === activeId ? '' : id);
    },
    [activeId, setPrimary, setActiveId]
  );

  const handleOnFocus = useCallback(
    (e: FocusEvent) => {
      setPrimary(e.target.id);
    },
    [setPrimary]
  );

  const handleClose = useCallback(
    (e: Event | FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        headerNavRef &&
        headerNavRef.current &&
        !headerNavRef.current.contains(target)
      ) {
        handleOnClick('');
      }
    },
    [headerNavRef, handleOnClick]
  );

  const nextSublink = useCallback(
    (id: string) => {
      const menu = getPrimaryMenu();
      if (menu) {
        const nextIndex = menu.links.findIndex(f => f.id === id) + 1;
        if (nextIndex < menu.links.length) {
          menu.links[nextIndex].focus();
        } else {
          nextPrimaryItem();
        }
      }
    },
    [getPrimaryMenu, nextPrimaryItem]
  );

  const previousSublink = useCallback(
    (id: string) => {
      const menu = getPrimaryMenu();
      if (menu) {
        const prevIndex = menu.links.findIndex(f => f.id === id) - 1;
        if (prevIndex >= 0) {
          menu.links[prevIndex].focus();
        } else {
          previousPrimaryItem();
        }
      }
    },
    [getPrimaryMenu, previousPrimaryItem]
  );

  const handleArrowRight = useCallback(
    (e: KeyboardEvent) => {
      const id = getActiveElement();
      if (isSublink(id)) {
        nextSublink(id);
      } else {
        nextPrimaryItem();
      }
      e.preventDefault();
    },
    [nextPrimaryItem, nextSublink]
  );

  const handleArrowDown = useCallback(
    (e: KeyboardEvent) => {
      const menu = getPrimaryMenu();
      if (menu && !activeId && menu.links.length > 0) {
        // open nav if it is closed
        setActiveId(menu.element.id);
      } else if (menu && activeId && menu.links.length > 0) {
        const id = getActiveElement();
        if (isSublink(id)) {
          nextSublink(id);
        } else {
          menu.links[0].focus();
        }
      }
      e.preventDefault();
    },
    [activeId, getPrimaryMenu, nextSublink]
  );

  const handleArrowLeft = useCallback(
    (e: KeyboardEvent) => {
      const id = getActiveElement();
      if (isSublink(id)) {
        previousSublink(id);
      } else {
        previousPrimaryItem();
      }
      e.preventDefault();
    },
    [previousSublink, previousPrimaryItem]
  );

  const handleArrowUp = useCallback(
    (e: KeyboardEvent) => {
      const id = getActiveElement();
      if (isSublink(id)) {
        const menu = getPrimaryMenu();
        if (menu) {
          const prevItemIndex = menu.links.findIndex(f => f.id === id) - 1;
          if (prevItemIndex >= 0) {
            menu.links[prevItemIndex].focus();
          } else {
            // at very beginning so close section
            setActiveId('');
            menu.element.focus();
          }
        }
        e.preventDefault();
      }
    },
    [getPrimaryMenu]
  );

  const handleTab = useCallback(
    (e: KeyboardEvent) => {
      const targetId = (e.target as HTMLElement).id;
      if (e.shiftKey) {
        if (activeId && !isSublink(targetId)) {
          const menu = menuRef.current?.find(p => p.element.id === targetId);
          if (
            menu &&
            menu.position - 1 >= 0 &&
            menuRef.current[menu.position - 1].links.length > 0
          ) {
            setActiveId(menuRef.current[menu.position - 1].element.id);
          } else {
            setActiveId('');
          }
        }
      } else {
        if (activeId && isSublink(targetId)) {
          // if mega menu is open and secondary item is active
          const menu = menuRef.current.find(p => p.element.id === activeId);
          if (menu) {
            const isLastSublink =
              menu.links.findIndex(f => f.id === targetId) ===
              menu.links.length - 1;
            const hasNextPrimaryLink =
              menu.position + 1 < menuRef.current.length;
            if (isLastSublink && hasNextPrimaryLink) {
              setActiveId(menuRef.current[menu.position + 1].element.id);
            }
          }
        }
      }
    },
    [activeId, setActiveId]
  );

  const initMegaLinks = useCallback(() => {
    // find links and build structure
    if (headerNavRef?.current) {
      const navSection = headerNavRef.current.childNodes[0].childNodes;
      const nav: MegaNavMenu = [];
      navSection.forEach((el, index) => {
        const primaryLink = el.childNodes[0] as HTMLElement;
        const secondary = el.childNodes[1] as HTMLElement;
        let secondaryLinks: HTMLElement[] = [];
        if (secondary) {
          secondaryLinks = Array.from(secondary.querySelectorAll('a, button'));
        }
        nav[index] = {
          element: primaryLink,
          links: secondaryLinks,
          position: index,
        };
      });
      menuRef.current = nav;
    }
  }, [headerNavRef]);

  const handleHeaderKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (menuRef.current.length === 0) {
        initMegaLinks();
      }
      if (isSearchField(getActiveElement())) return;

      switch (e.key) {
        case 'ArrowDown':
          handleArrowDown(e);
          break;
        case 'ArrowUp':
          handleArrowUp(e);
          break;
        case 'ArrowRight':
          handleArrowRight(e);
          break;
        case 'ArrowLeft':
          handleArrowLeft(e);
          break;
        case 'Escape':
          if (activeId) {
            const menu = getPrimaryMenu();
            if (menu) menu.element.focus();
            setActiveId('');
          }
          break;
        case 'Tab':
          handleTab(e);
          break;
      }
    },
    [
      activeId,
      getPrimaryMenu,
      handleArrowLeft,
      handleArrowUp,
      handleArrowRight,
      handleArrowDown,
      handleTab,
      initMegaLinks,
    ]
  );

  useEffect(() => {
    document.addEventListener('click', handleClose, false);
    return () => {
      document.removeEventListener('click', handleClose, false);
    };
  }, [handleClose]);

  return {
    activeId,
    handleClose,
    handleOnFocus,
    handleOnClick,
    handleHeaderKeyDown,
  };
};
